import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useMfaMetricas } from '@/shared/actions/erp/administration/mfa-admin';

const Tarjeta = ({
  titulo,
  valor,
  detalle,
  color = 'text.primary',
}: {
  titulo: string;
  valor: React.ReactNode;
  detalle?: string;
  color?: string;
}) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {titulo}
      </Typography>
      <Typography variant="h4" sx={{ color, mt: 0.5 }}>
        {valor}
      </Typography>
      {detalle && (
        <Typography variant="caption" color="text.secondary">
          {detalle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export const MfaAdminMetricasTab = () => {
  const { data, isLoading } = useMfaMetricas();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data) {
    return <Alert severity="error">No se pudieron cargar las métricas.</Alert>;
  }

  const cobertura = data.porcentaje_cobertura;

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Tarjeta
            titulo="Usuarios aplicables"
            valor={data.total_usuarios_aplicables}
            detalle="(excluye usuarios de integración)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Tarjeta
            titulo="Con 2FA activo"
            valor={data.activos}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Tarjeta
            titulo="Sin 2FA"
            valor={data.sin_2fa}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Tarjeta
            titulo="Bloqueados por intentos"
            valor={data.bloqueados}
            color={data.bloqueados ? 'error.main' : 'text.primary'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Tarjeta titulo="Forzados" valor={data.forzados} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Tarjeta
            titulo="Usuarios de integración"
            valor={data.usuarios_integracion}
            detalle="no usan 2FA"
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Cobertura
              </Typography>
              <Typography variant="h4" mt={0.5}>
                {cobertura}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={cobertura}
                sx={{ mt: 1, height: 8, borderRadius: 1 }}
                color={
                  cobertura >= 80 ? 'success' : cobertura >= 50 ? 'warning' : 'error'
                }
              />
              <Typography variant="caption" color="text.secondary">
                {data.activos} de {data.total_usuarios_aplicables} usuarios
                tienen 2FA activo.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Distribución por método (activos)
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Método</TableCell>
                  <TableCell align="right">Usuarios</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.por_metodo.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography variant="caption" color="text.secondary">
                        Sin datos
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.por_metodo.map((m) => (
                    <TableRow key={m.dos_factor_metodo || 'sin'}>
                      <TableCell>
                        {m.dos_factor_metodo === 'app'
                          ? 'App Autenticadora'
                          : m.dos_factor_metodo === 'email'
                          ? 'Email'
                          : '-'}
                      </TableCell>
                      <TableCell align="right">{m.total}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Eventos últimos 30 días
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Evento</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.eventos_ultimos_30_dias.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography variant="caption" color="text.secondary">
                        Sin eventos
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.eventos_ultimos_30_dias.map((e) => (
                    <TableRow key={e.evento}>
                      <TableCell>{e.evento}</TableCell>
                      <TableCell align="right">{e.total}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
};
